import React, { useEffect } from 'react';
import AOS from 'aos';
import { InstagramSVG, LinkedInSVG, TikTokSVG } from '../../component/SVGs/SocialSVGs';

const signatureEvents = [
  {
    code: "ASO",
    name: "Airlangga Statistics Olympiad",
    description:
      "A prestigious national olympiad testing statistical thinking and problem-solving skills for high school and university students.",
    badgeLabel: "Olympiad",
    badgeColor: "bg-arsen-quaternary-50 text-arsen-quaternary-800 border-arsen-quaternary-200",
  },
  {
    code: "ASEC",
    name: "Airlangga Statistics Essay Competition",
    description:
      "Express your ideas and critical analysis of real-world issues through statistics-driven research and essays.",
    badgeLabel: "Essay",
    badgeColor: "bg-arsen-primary-50 text-arsen-primary-800 border-arsen-primary-200",
  },
  {
    code: "ASIC",
    name: "Airlangga Statistics Infographic Competition",
    description:
      "Translate complex statistical data into visually stunning, informative, and impactful creative infographics.",
    badgeLabel: "Infographic",
    badgeColor: "bg-arsen-secondary-50 text-arsen-secondary-800 border-arsen-secondary-200",
  },
  {
    code: "AVE",
    name: "Airlangga Venue & Exhibition",
    description:
      "The peak event of ARSEN — national seminars, data exhibitions, and interactive statistical talkshows.",
    badgeLabel: "Seminar & Expo",
    badgeColor: "bg-arsen-tertiary-50 text-arsen-tertiary-800 border-arsen-tertiary-200",
  },
];

function ComingSoon() {
  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: 'ease-out' });
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-gray-900 flex flex-col font-sans selection:bg-arsen-primary-100">

      {/* ── Header ── */}
      <header className="w-full max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img
            src="/assets/img/logo/arsen2026-icon.png"
            alt="ARSEN Logo"
            className="w-9 h-9 object-contain"
          />
          <span className="text-lg font-bold tracking-wide text-arsen-primary">
            ARSΣN UNAIR
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <a
            href="https://www.instagram.com/arsenunair"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon p-2 flex items-center justify-center"
            title="Instagram"
          >
            <InstagramSVG width="16" height="16" />
          </a>
          <a
            href="https://www.linkedin.com/company/linkedinairlanggastatisticsevent/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon p-2 flex items-center justify-center"
            title="LinkedIn"
          >
            <LinkedInSVG width="16" height="16" />
          </a>
          <a
            href="https://www.tiktok.com/@arsenunair"
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon p-2 flex items-center justify-center"
            title="TikTok"
          >
            <TikTokSVG width="16" height="16" />
          </a>
        </div>
      </header>

      {/* ── Hero ── */}
      <main className="flex-grow flex flex-col items-center justify-center px-6 md:px-10 pt-8 pb-16">
        <div className="max-w-2xl text-center flex flex-col items-center">
          {/* Logo mark */}
          <div data-aos="fade-down" className="mb-6 relative">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl bg-arsen-primary-50 border border-arsen-primary-100 flex items-center justify-center shadow-sm">
              <img
                src="/assets/img/logo/arsen2026-icon.png"
                alt="ARSEN 2026"
                className="w-20 h-20 md:w-24 md:h-24 object-contain"
              />
            </div>
            <img
              src="/assets/img/mascot/mascot4.svg"
              alt=""
              className="absolute -right-14 -bottom-4 w-20 md:w-24 h-auto hidden sm:block"
            />
          </div>

          {/* Status label */}
          <div data-aos="fade-up" data-aos-delay="50" className="mb-5">
            <span className="inline-block px-4 py-1.5 rounded-full border border-arsen-primary-200 bg-arsen-primary-50 text-arsen-primary text-xs font-semibold tracking-wide uppercase">
              Coming Soon — 2026
            </span>
          </div>

          {/* Title */}
          <h1
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight text-arsen-primary-950 mb-4"
          >
            Airlangga Statistics{' '}
            <span className="text-arsen-primary">Events</span>
          </h1>

          {/* Tagline */}
          <p
            data-aos="fade-up"
            data-aos-delay="150"
            className="text-gray-500 text-base md:text-lg leading-relaxed max-w-lg mx-auto mb-10"
          >
            The biggest statistical competition and seminar series hosted by
            Universitas Airlangga is back. Dates will be announced soon.
          </p>
        </div>

        {/* ── Event Previews ── */}
        <section className="w-full max-w-4xl mt-16">
          <h2
            data-aos="fade-up"
            className="text-center text-sm font-semibold tracking-widest text-gray-400 uppercase mb-8"
          >
            What to Expect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {signatureEvents.map((evt, idx) => (
              <div
                key={evt.code}
                data-aos="fade-up"
                data-aos-delay={idx * 80}
                className="event-card p-6 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xl font-black tracking-wider text-arsen-primary">
                      {evt.code}
                    </span>
                    <span
                      className={`text-[10px] px-2.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${evt.badgeColor}`}
                    >
                      {evt.badgeLabel}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-800 mb-1.5">
                    {evt.name}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {evt.description}
                  </p>
                </div>
                <div className="mt-5 text-xs font-medium text-arsen-primary-600">
                  Details coming soon →
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="w-full max-w-6xl mx-auto px-6 md:px-10 py-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-400">
        <div>&copy; 2026 Airlangga Statistics Events (ARSEN)</div>
        <div className="flex items-center space-x-5">
          <a
            href="https://www.tiktok.com/@arsenunair"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 transition-colors"
          >
            TikTok
          </a>
          <a
            href="mailto:arsenunair2026@gmail.com"
            className="hover:text-gray-700 transition-colors"
          >
            Email
          </a>
          <a
            href="https://himastaunair.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 transition-colors"
          >
            HIMASTA UNAIR
          </a>
        </div>
      </footer>
    </div>
  );
}

export default ComingSoon;
